import { AREA_FEED_MODEL, EVENT_SEVERITY, EVENT_TYPE, EVENT_TYPE_EMPTY_TRANS, EVENT_TYPE_PANEL_EVENT_TYPES, EVENT_TYPE_PANEL_EXCLUDED_SEVERITIES, getPanelType, HEADER_PICTOGRAM_DEFAULT, PANEL_TYPE, PANEL_VIEW, UNCONFIGURED_PANEL, ZONE_OVERVIEW_ALERT_SEVERITIES } from "../Model/Model";
import { userType } from "../../../../enums/userTypeEnum";

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Logic shared by the panels, the expanded view and the modals: it resolves what a panel shows
 * and which of the events received by the socket belong to it.
 */

/**
 * A zone panel without zone is the zone overview: one group per zone of the admin.
 */
export const isZoneOverviewPanel = (panel) => panel?.typeId === PANEL_TYPE.ZONE.ID && !panel?.sourceId;

export const isConfiguredPanel = (panel) => !!panel?.typeId && (!!panel?.sourceId || isZoneOverviewPanel(panel));

/**
 * The reports panels show the reports of the notifications; the rest read their events from the backend.
 */
export const isReportsPanel = (panel) => panel?.typeId === PANEL_TYPE.EVENT.ID && panel?.sourceId === EVENT_TYPE.REPORTS;

/**
 * Source of the events read for a panel.
 */
export const getPanelSourceKey = (panel) => `${panel?.typeId}_${panel?.sourceId}`;

/**
 * Joins two lists of events, newest first, without repeating any of them.
 */
export const mergeEvents = (events = [], incoming = []) => {
    const ids = new Set(incoming.map((event) => event.id));
    return [...incoming, ...events.filter((event) => !ids.has(event.id))].sort((a, b) => b.date - a.date);
};

/**
 * Date the next page of a panel is read from: the oldest cursor of its events. The reports are sorted by
 * their end but read by their start, so the last event is not always the oldest one.
 */
export const getOldestCursor = (events = []) => events.reduce((oldest, event) => (oldest == null || event.cursor < oldest ? event.cursor : oldest), null);

/**
 * First page read again over the events a panel already shows. The page replaces the newest events, so the
 * events shown take what is stored, and the events older than the page are kept,
 * so a panel scrolled down does not lose its position. They are kept only when the page reaches them (it
 * shares an event with them, or it is the last page); otherwise more events than a page arrived in between
 * and they are dropped, so no gap is left in the list.
 */
export const replaceFirstPage = (events = [], page = [], pageSize) => {
    const ids = new Set(page.map((event) => event.id));
    const oldest = getOldestCursor(page);
    const reachesEvents = page.length < pageSize || events.some((event) => ids.has(event.id));
    if (oldest == null || !reachesEvents) return page;

    return [...page, ...events.filter((event) => event.cursor < oldest)];
};

/**
 * Text of the empty state of the panel: the one of its event type, or the one of its panel type.
 */
export const getPanelEmptyText = (panel, t) => {
    if (isZoneOverviewPanel(panel)) return t(getPanelType(panel.typeId).OVERVIEW_EMPTY_TRANS);
    return t(
        panel?.typeId === PANEL_TYPE.EVENT.ID
            ? EVENT_TYPE_EMPTY_TRANS[panel?.sourceId] ?? 'no_records_available'
            : getPanelType(panel?.typeId)?.EMPTY_TRANS ?? 'no_records_available'
    );
};

/**
 * Way the panel draws its events.
 */
export const getPanelView = (panel) => {
    if (!isConfiguredPanel(panel)) return PANEL_VIEW.UNCONFIGURED;
    return isZoneOverviewPanel(panel) ? PANEL_VIEW.GROUPED : getPanelType(panel.typeId).VIEW;
};


/**
 * Header of the panel: its icon, the colors of its pictogram and its title, already translated. The event types are sent by
 * their name, so it is translated.
 */
export const getPanelHeader = (panel, t) => {
    if (!isConfiguredPanel(panel)) {
        return { Icon: UNCONFIGURED_PANEL.Icon, pictogram: HEADER_PICTOGRAM_DEFAULT, title: t(UNCONFIGURED_PANEL.TRANS) };
    }

    const type = getPanelType(panel.typeId);
    if (isZoneOverviewPanel(panel)) return { Icon: type.Icon, pictogram: HEADER_PICTOGRAM_DEFAULT, title: t(type.OVERVIEW_TITLE) };

    // The zone panels show the path of their zone, "Building A · Floor 3" //
    const sourceName = panel.typeId === PANEL_TYPE.EVENT.ID ? t(panel.sourceName?.toLowerCase()) : (panel.sourcePath ?? panel.sourceName);
    // A user panel starts its title with the type of its user, "Employee · name", instead of the panel type //
    const userTypeName = panel.typeId === PANEL_TYPE.USER.ID ? userType[panel.userTypeId] : null;
    const titlePrefix = userTypeName ?? type.TITLE;

    return {
        Icon: type.Icon,
        pictogram: type.HEADER_SEVERITY ?? HEADER_PICTOGRAM_DEFAULT,
        title: titlePrefix ? `${t(titlePrefix)} · ${sourceName ?? ''}` : sourceName,
    };
};

/**
 * Group of the zone overview an event belongs to: the zone of the admin among the zone of the event and its parents.
 */
const getOverviewAreaId = (areas = []) => (event) => areas.find((area) => (event.zoneIds ?? []).includes(area.id))?.id ?? null;

/**
 * Events the zone overview shows: the critical alerts, the CRITICAL and WARNING logs of the controllers.
 */
const isOverviewEvent = (event) => !!event.controllerLog && ZONE_OVERVIEW_ALERT_SEVERITIES.includes(event.severity);

/**
 * Events that belong to the panel source, newest first. The zone overview takes the critical alerts of the zones
 * of the admin, its groups (areas); a zone panel only takes the accesses of its zone.
 */
export const getPanelEvents = (panel, events = [], areas = []) => {
    if (!isConfiguredPanel(panel)) return [];

    switch (panel.typeId) {
        case PANEL_TYPE.ZONE.ID:
            if (isZoneOverviewPanel(panel)) return events.filter((event) => isOverviewEvent(event) && getOverviewAreaId(areas)(event) !== null);
            return events.filter((event) => !event.controllerLog && (event.zoneIds ?? []).includes(panel.sourceId));
        case PANEL_TYPE.USER.ID:
            return events.filter((event) => event.userId === panel.sourceId);
        case PANEL_TYPE.DEVICE.ID:
            return events.filter((event) => event.deviceId === panel.sourceId);
        case PANEL_TYPE.EVENT.ID:
            return events.filter((event) => (EVENT_TYPE_PANEL_EVENT_TYPES[panel.sourceId] ?? [panel.sourceId]).includes(event.eventType)
                && !(EVENT_TYPE_PANEL_EXCLUDED_SEVERITIES[panel.sourceId] ?? []).includes(event.severity));
        default:
            return [];
    }
};

/**
 * Adds the new alerts of the socket to the counters of the groups of the zone overview: the zone of the admin among
 * the zone of the alert and its parents. Every group is read with the panel, an alert of another zone is left out.
 */
export const addEventsToAreas = (areas = [], events = []) => {
    const getAreaId = getOverviewAreaId(areas);
    const next = new Map(areas.map((area) => [area.id, area]));
    events.forEach((event) => {
        const area = next.get(getAreaId(event));
        if (!area) return;
        next.set(area.id, {
            ...area,
            total: area.total + 1,
            critical: area.critical + (event.severity === EVENT_SEVERITY.CRITICAL.ID ? 1 : 0),
            warning: area.warning + (event.severity === EVENT_SEVERITY.WARNING.ID ? 1 : 0),
            lastAt: Math.max(area.lastAt, event.date),
        });
    });
    return [...next.values()];
};

/**
 * Adds the alerts of the socket to the groups of the zone overview that already read their alerts. A group not read
 * yet receives them with its first page.
 */
export const addEventsToAreaFeeds = (areaFeeds = {}, events = [], areas = []) => {
    const getAreaId = getOverviewAreaId(areas);
    return events.reduce((next, event) => {
        const areaId = getAreaId(event);
        const feed = next[areaId];
        return feed?.loaded ? { ...next, [areaId]: { ...feed, events: mergeEvents(feed.events, [event]) } } : next;
    }, areaFeeds);
};

/**
 * Group cards of the zone overview: every zone of the admin with the counters of today and the alerts it read
 * by its own pages. The groups keep the order of their newest alert.
 */
export const getAreaGroups = (areas = [], areaFeeds = {}) => [...areas]
    .sort((a, b) => b.lastAt - a.lastAt)
    .map((area) => ({ ...area, feed: areaFeeds[area.id] ?? AREA_FEED_MODEL }));

export const getSeverity = (severityId) => EVENT_SEVERITY[severityId] ?? EVENT_SEVERITY.NEUTRAL;
